<p-toast></p-toast>

<div class="fadein animation-duration-200 posm-overview-page">
    <app-page-metric-strip [metrics]="metrics()"></app-page-metric-strip>

    <section class="summary-card-grid mt-4">
        @for (card of summaryCards(); track card.title) {
            <article class="overview-card" [ngClass]="'tone-' + card.tone">
                <div class="overview-card__header">
                    <div>
                        <h3>{{ card.title }}</h3>
                        <p>{{ card.subtitle }}</p>
                    </div>
                    <p-tag [value]="card.badge" [severity]="card.badgeSeverity"></p-tag>
                </div>

                <div class="overview-card__stats">
                    @for (stat of card.stats; track stat.label) {
                        <div class="overview-stat">
                            <span class="overview-stat__label">{{ stat.label }}</span>
                            <strong class="overview-stat__value">{{ stat.value }}</strong>
                            @if (stat.hint) {
                                <small class="overview-stat__hint">{{ stat.hint }}</small>
                            }
                        </div>
                    }
                </div>

                @if (card.note) {
                    <div class="overview-card__note">{{ card.note }}</div>
                }
            </article>
        }
    </section>

    @if (validationIssues().length) {
        <app-page-data-panel
            title="Project Validation"
            subtitle="Store, schedule, and transport readiness issues still blocking execution."
            [resultText]="criticalValidationCount() + ' critical'"
            class="block mt-4">
            <div class="validation-grid">
                @for (issue of validationIssues().slice(0, 8); track issue.type + '-' + issue.resourceId + '-' + issue.message) {
                    <article class="validation-card">
                        <div class="validation-card__header">
                            <strong>{{ issue.label }}</strong>
                            <p-tag
                                [value]="issue.severity || 'INFO'"
                                [severity]="issue.severity === 'ERROR' ? 'danger' : 'warning'">
                            </p-tag>
                        </div>
                        <div class="validation-card__message">{{ issue.message }}</div>
                    </article>
                }
            </div>
        </app-page-data-panel>
    }

    <div class="grid mt-4">
        <div class="col-12 xl:col-7">
            <app-page-data-panel
                title="Target Store List"
                subtitle="Target stores, mapped LogiLink locations, delivery dates, and contact readiness."
                [resultText]="stores().length + ' stores'">
                <p-table
                    [value]="stores()"
                    [loading]="storesLoading()"
                    styleClass="p-datatable-sm no-borders"
                    [scrollable]="true"
                    scrollHeight="26rem">
                    <ng-template pTemplate="header">
                        <tr>
                            <th>Store</th>
                            <th>LogiLink Location</th>
                            <th>Planned</th>
                            <th>Contact</th>
                            <th>Issues</th>
                            <th class="text-right">Action</th>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-store>
                        <tr>
                            <td>
                                <div class="font-bold text-900">{{ storeName(store) }}</div>
                                <div class="text-xs text-500 mt-1">Store # {{ store.storeNumber || '-' }}</div>
                                <div class="text-xs text-500 mt-1">{{ storeArea(store) }}</div>
                            </td>
                            <td>
                                <div class="flex flex-wrap align-items-center gap-2">
                                    <p-tag [value]="store.location?.id ? ('Location #' + store.location.id) : 'Location pending'" severity="info"></p-tag>
                                    <p-tag [value]="locationType(store)" severity="secondary"></p-tag>
                                </div>
                                <div class="text-xs text-500 mt-1">Ref: {{ locationReference(store) }}</div>
                                <div class="text-xs mt-1">
                                    <p-tag [value]="locationCoordinateText(store)" [severity]="locationCoordinateSeverity(store)"></p-tag>
                                </div>
                            </td>
                            <td>
                                <div class="font-medium text-900">{{ formattedDate(store.plannedDate) }}</div>
                                <div class="text-xs text-500 mt-1">{{ locationAddress(store) }}</div>
                            </td>
                            <td>
                                <div class="text-sm text-900">{{ store.contactPersonName || '-' }}</div>
                                <div class="text-xs text-500 mt-1">{{ store.contactPersonPhone || 'No phone' }}</div>
                            </td>
                            <td>
                                @if (storeIssueCount(store) > 0) {
                                    <p-tag [value]="storeIssueCount(store) + ' issue(s)'" severity="warning"></p-tag>
                                } @else {
                                    <p-tag value="Ready" severity="success"></p-tag>
                                }
                            </td>
                            <td class="text-right">
                                <p-button
                                    icon="pi pi-pencil"
                                    severity="secondary"
                                    [outlined]="true"
                                    [rounded]="true"
                                    (click)="openStoreEditor(store)">
                                </p-button>
                            </td>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="emptymessage">
                        <tr>
                            <td colspan="6" class="text-center p-6 text-500">No stores uploaded for this project.</td>
                        </tr>
                    </ng-template>
                </p-table>
            </app-page-data-panel>
        </div>

        <div class="col-12 xl:col-5">
            <app-page-data-panel
                title="Project Attachments"
                subtitle="Store lists, artwork, mockups, approvals, and operations documents."
                [resultText]="attachments().length + ' files'">
                <div class="panel-scroll-4 panel-scroll-attachments">
                    @if (attachmentCategorySummary().length) {
                        <div class="attachment-category-strip">
                            @for (entry of attachmentCategorySummary(); track entry.label) {
                                <div class="attachment-category-pill">
                                    <span>{{ entry.label }}</span>
                                    <strong>{{ entry.count }}</strong>
                                </div>
                            }
                        </div>
                    }

                    <div class="attachment-toolbar">
                        <div class="attachment-toolbar__category">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="pi pi-tags"></i></p-inputGroupAddon>
                                <p-iftalabel>
                                    <p-select
                                        inputId="attachmentCategory"
                                        [options]="attachmentCategoryOptions()"
                                        optionLabel="label"
                                        optionValue="value"
                                        [(ngModel)]="attachmentDraft.category"
                                        appendTo="body"
                                        styleClass="w-full">
                                    </p-select>
                                    <label for="attachmentCategory">Category</label>
                                </p-iftalabel>
                            </p-inputGroup>
                        </div>

                        <div class="attachment-toolbar__upload">
                            <p-fileUpload
                                mode="basic"
                                chooseLabel="Upload File"
                                chooseIcon="pi pi-upload"
                                [auto]="true"
                                [customUpload]="true"
                                [disabled]="attachmentUploading() || !project().id"
                                (onSelect)="uploadAttachmentFile($event)">
                            </p-fileUpload>
                        </div>
                    </div>

                    @if (attachmentUploading() || attachmentUploadProgress() > 0) {
                        <div class="attachment-upload-state">
                            <div>
                                <strong>Uploading attachment</strong>
                                <span>{{ attachmentUploadProgress() }}% complete</span>
                            </div>
                            <p-progressBar [value]="attachmentUploadProgress()"></p-progressBar>
                        </div>
                    }

                    @if (attachments().length) {
                        <div class="file-grid mt-4">
                            @for (attachment of attachments(); track attachment.id || attachment.fileUrl || $index) {
                                <div class="file-tile">
                                    <a [href]="attachment.fileUrl" target="_blank">
                                        <div class="file-tile__header">
                                            <i class="pi pi-file"></i>
                                            <div class="file-tile__meta">
                                                <strong>{{ attachment.fileName || 'Attachment' }}</strong>
                                                <span>{{ attachmentFileKind(attachment) }}</span>
                                            </div>
                                        </div>
                                        <small>{{ formattedDate(attachment.createdAt) }}</small>
                                        <div class="file-scope-tag">
                                            <i class="pi pi-tag"></i>
                                            <div>
                                                <strong>{{ attachmentCategoryLabel(attachment.category) }}</strong>
                                                <span>{{ attachment.fileSize ? (attachment.fileSize / 1024 / 1024 | number:'1.0-2') + ' MB' : 'Project file' }}</span>
                                            </div>
                                        </div>
                                    </a>
                                    <button
                                        pButton
                                        type="button"
                                        icon="pi pi-trash"
                                        severity="danger"
                                        [rounded]="true"
                                        [text]="true"
                                        (click)="deleteAttachment(attachment)">
                                    </button>
                                </div>
                            }
                        </div>
                    } @else {
                        <div class="empty-state compact-empty mt-4">
                            <i class="pi pi-paperclip"></i>
                            <strong>No project files linked</strong>
                            <span>Upload store lists, artwork, approvals, and operations documents directly here.</span>
                        </div>
                    }
                </div>
            </app-page-data-panel>
        </div>
    </div>

    <div class="grid mt-4">
        <div class="col-12 xl:col-7">
            <app-page-data-panel
                title="Material Readiness"
                subtitle="Requested items, physical reservation or logical source coverage, and delivery-schedule allocation state."
                [resultText]="totalReservedQuantity() + ' covered / ' + totalRequestedQuantity() + ' required'">
                <div class="panel-scroll-4 panel-scroll-materials">
                    @if (materialRows().length) {
                        <div class="readiness-table">
                            <div class="readiness-table__header material-grid">
                                <span>Item</span>
                                <span>Requested</span>
                                <span>Reserved</span>
                                <span>Allocated</span>
                                <span>Gap</span>
                                <span>Status</span>
                            </div>

                            @for (row of materialRows(); track row.item.id || row.item.inventoryItem?.id || $index) {
                                <article class="readiness-row material-grid">
                                    <div class="item-profile">
                                        <strong>{{ itemName(row.item) }}</strong>

                                        <div class="code-chip-row">
                                            @if (row.item.inventoryItem?.itemNumber) {
                                                <span class="code-chip">{{ row.item.inventoryItem?.itemNumber }}</span>
                                            }
                                            @if (row.item.inventoryItem?.skuNumber) {
                                                <span class="code-chip">SKU {{ row.item.inventoryItem?.skuNumber }}</span>
                                            }
                                            @if (row.item.inventoryItem?.customerItemNumber) {
                                                <span class="code-chip">Customer {{ row.item.inventoryItem?.customerItemNumber }}</span>
                                            }
                                        </div>

                                        <div class="metadata-line">{{ itemPhysicalMeta(row.item) }}</div>
                                        <div class="metadata-line">{{ itemHierarchyMeta(row.item) }}</div>
                                        @if (row.detail) {
                                            <div class="detail-line">{{ row.detail }}</div>
                                        }
                                    </div>

                                    <strong class="qty-cell">{{ row.requested }}</strong>
                                    <strong class="qty-cell">{{ row.reserved }}</strong>
                                    <strong class="qty-cell">{{ row.allocated }}</strong>
                                    <strong class="qty-cell" [class.text-red-600]="row.gap > 0">{{ row.gap }}</strong>
                                    <div class="status-cell">
                                        <p-tag [value]="row.status" [severity]="materialStatusSeverity(row.status)"></p-tag>
                                    </div>
                                </article>
                            }
                        </div>
                    } @else {
                        <div class="empty-state compact-empty">
                            <i class="pi pi-box"></i>
                            <strong>No POSM material lines</strong>
                            <span>Item-level source coverage will appear here after materials are captured.</span>
                        </div>
                    }
                </div>
            </app-page-data-panel>
        </div>

        <div class="col-12 xl:col-5">
            <app-page-data-panel
                title="Delivery Schedule Workspace"
                subtitle="Rows ranked by readiness risk across coverage, allocation, release, and transport execution."
                [resultText]="scheduleCount() + ' schedule rows'">
                <div class="panel-scroll-4 panel-scroll-schedules">
                    @if (scheduleHeadlineRows().length) {
                        <div class="schedule-list">
                            @for (row of scheduleHeadlineRows(); track row.id || row.scheduleNumber || $index) {
                                <article class="schedule-card">
                                    <div class="schedule-card__header">
                                        <div>
                                            <strong>{{ scheduleStoreLabel(row) }}</strong>
                                            <div class="metadata-line">{{ scheduleStoreMeta(row) }}</div>
                                        </div>
                                        <p-tag
                                            [value]="scheduleProgressLabel(row)"
                                            [severity]="scheduleProgressSeverity(row)">
                                        </p-tag>
                                    </div>

                                    <div class="schedule-card__item">
                                        <div class="item-title">{{ scheduleItemLabel(row) }}</div>
                                        <div class="metadata-line">{{ scheduleItemMeta(row) || 'Material code pending' }}</div>
                                    </div>

                                    <div class="schedule-card__stats">
                                        <div>
                                            <span>Allocation</span>
                                            <p-tag
                                                [value]="row.allocationStatus || 'NOT_ALLOCATED'"
                                                [severity]="scheduleAllocationSeverity(row.allocationStatus)">
                                            </p-tag>
                                        </div>
                                        <div>
                                            <span>Readiness</span>
                                            <strong>{{ row.readinessStatus || 'PENDING' }}</strong>
                                        </div>
                                        <div>
                                            <span>Release</span>
                                            <strong>{{ row.releaseNumber || row.releaseStatus || 'Not released' }}</strong>
                                        </div>
                                        <div>
                                            <span>Shipment</span>
                                            <strong>{{ row.deliveryNumber || row.shipmentStatus || row.transportStatus || '-' }}</strong>
                                        </div>
                                    </div>

                                    @if ((row.mappingWarnings?.length || 0) + (row.allocationWarnings?.length || 0) > 0 || row.nextAction) {
                                        <div class="schedule-card__footnote">
                                            {{ row.nextAction || ((row.mappingWarnings?.length || 0) + (row.allocationWarnings?.length || 0)) + ' unresolved warnings' }}
                                        </div>
                                    }
                                </article>
                            }
                        </div>
                    } @else {
                        <div class="empty-state compact-empty">
                            <i class="pi pi-calendar-clock"></i>
                            <strong>No delivery schedules</strong>
                            <span>Schedule readiness, releases, and tracking signals will appear after schedule rows are created.</span>
                        </div>
                    }
                </div>
            </app-page-data-panel>
        </div>
    </div>
</div>

<p-dialog
    header="Edit Store Plan"
    [visible]="editStoreVisible()"
    (visibleChange)="editStoreVisible.set($event)"
    [modal]="true"
    [style]="{ width: '720px', maxWidth: '95vw' }">
    <div class="grid">
        <div class="col-12">
            <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-map"></i></p-inputGroupAddon>
                <p-iftalabel>
                    <p-select
                        inputId="editLogilinkLocation"
                        appLazyLocationPicker
                        [appLazyLocationPicker]="projectCompanyId()"
                        optionLabel="name"
                        optionValue="id"
                        [(ngModel)]="selectedStoreLocationId"
                        (ngModelChange)="onStoreLocationChange($event)"
                        [filter]="true"
                        filterBy="name,externalLocationReference,shortDescription,longDescription"
                        appendTo="body"
                        styleClass="w-full">
                    </p-select>
                    <label for="editLogilinkLocation">LogiLink Location</label>
                </p-iftalabel>
            </p-inputGroup>
        </div>

        <div class="col-12 md:col-6">
            <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-map-marker"></i></p-inputGroupAddon>
                <p-iftalabel>
                    <input id="editStoreName" pInputText [(ngModel)]="editStoreDraft.location.name" class="w-full">
                    <label for="editStoreName">Store Location Name</label>
                </p-iftalabel>
            </p-inputGroup>
        </div>

        <div class="col-12 md:col-6">
            <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-hashtag"></i></p-inputGroupAddon>
                <p-iftalabel>
                    <input id="editStoreNumber" pInputText [(ngModel)]="editStoreDraft.storeNumber" class="w-full">
                    <label for="editStoreNumber">Store Number</label>
                </p-iftalabel>
            </p-inputGroup>
        </div>

        <div class="col-12 md:col-6">
            <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-id-card"></i></p-inputGroupAddon>
                <p-iftalabel>
                    <input id="editLocationReference" pInputText [(ngModel)]="editStoreDraft.location.externalLocationReference" class="w-full">
                    <label for="editLocationReference">Location Reference</label>
                </p-iftalabel>
            </p-inputGroup>
        </div>

        <div class="col-12 md:col-6">
            <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-building"></i></p-inputGroupAddon>
                <p-iftalabel>
                    <input id="editStoreCity" pInputText [(ngModel)]="editStoreDraft.city" class="w-full">
                    <label for="editStoreCity">City</label>
                </p-iftalabel>
            </p-inputGroup>
        </div>

        <div class="col-12">
            <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-map"></i></p-inputGroupAddon>
                <p-iftalabel>
                    <input id="editStoreAddress" pInputText [(ngModel)]="editStoreDraft.address" class="w-full">
                    <label for="editStoreAddress">Location Address</label>
                </p-iftalabel>
            </p-inputGroup>
        </div>

        <div class="col-12 md:col-6">
            <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-calendar"></i></p-inputGroupAddon>
                <p-iftalabel>
                    <p-datePicker inputId="editPlannedDate" [(ngModel)]="editStoreDraft.plannedDate" appendTo="body" styleClass="w-full"></p-datePicker>
                    <label for="editPlannedDate">Install / Delivery Date</label>
                </p-iftalabel>
            </p-inputGroup>
        </div>

        <div class="col-12 md:col-6">
            <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-user"></i></p-inputGroupAddon>
                <p-iftalabel>
                    <input id="editContactName" pInputText [(ngModel)]="editStoreDraft.contactPersonName" class="w-full">
                    <label for="editContactName">Contact Name</label>
                </p-iftalabel>
            </p-inputGroup>
        </div>

        <div class="col-12 md:col-6">
            <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-phone"></i></p-inputGroupAddon>
                <p-iftalabel>
                    <input id="editContactPhone" pInputText [(ngModel)]="editStoreDraft.contactPersonPhone" class="w-full">
                    <label for="editContactPhone">Contact Phone</label>
                </p-iftalabel>
            </p-inputGroup>
        </div>

        <div class="col-12 md:col-6">
            <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-compass"></i></p-inputGroupAddon>
                <p-iftalabel>
                    <p-inputNumber inputId="editLatitude" [(ngModel)]="editLatitude" [minFractionDigits]="4" [maxFractionDigits]="8" inputStyleClass="w-full"></p-inputNumber>
                    <label for="editLatitude">Latitude</label>
                </p-iftalabel>
            </p-inputGroup>
        </div>

        <div class="col-12 md:col-6">
            <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-compass"></i></p-inputGroupAddon>
                <p-iftalabel>
                    <p-inputNumber inputId="editLongitude" [(ngModel)]="editLongitude" [minFractionDigits]="4" [maxFractionDigits]="8" inputStyleClass="w-full"></p-inputNumber>
                    <label for="editLongitude">Longitude</label>
                </p-iftalabel>
            </p-inputGroup>
        </div>

        <div class="col-12">
            <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-align-left"></i></p-inputGroupAddon>
                <p-iftalabel>
                    <textarea id="editStoreComments" pTextarea rows="3" [(ngModel)]="editStoreDraft.comments" class="w-full"></textarea>
                    <label for="editStoreComments">Comments</label>
                </p-iftalabel>
            </p-inputGroup>
        </div>
    </div>

    <ng-template pTemplate="footer">
        <p-button label="Cancel" icon="pi pi-times" severity="secondary" [outlined]="true" (click)="editStoreVisible.set(false)"></p-button>
        <p-button label="Save Store" icon="pi pi-save" (click)="saveStore()"></p-button>
    </ng-template>
</p-dialog>
